import { useEffect, useState } from "react";
import { getClient, getClientDeals, getClientTimeline, deleteClient } from "../api/clients";
import EditClientModal from "../components/clients/EditClientModal";
import { useParams, useNavigate } from "react-router-dom";
import Tag from "../components/ui/Tag";
import Timeline from "../components/ui/Timeline";
import ClickToCall from "../components/Telephony/ClickToCall";
import api from "../api/axios";
import { getRole } from "../hooks/usePermissions";
import "../styles/pages/client-page.css";

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: "Активна",    color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  won:    { label: "Завершена",  color: "#818cf8", bg: "rgba(129,140,248,0.12)" },
  lost:   { label: "Проиграна", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
};

const PAY_LABELS: Record<string, { label: string; icon: string }> = {
  card:     { label: "Карта",    icon: "💳" },
  kaspi:    { label: "Kaspi QR", icon: "🟡" },
  halyk:    { label: "Halyk QR", icon: "🟢" },
  transfer: { label: "Безнал",   icon: "🏦" },
};

export default function ClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client, setClient]   = useState<any>(null);
  const [deals, setDeals]     = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"deals" | "history" | "comments" | "bonus">("deals");
  const [showEdit, setShowEdit] = useState(false);
  const [loyalty, setLoyalty] = useState<any>(null);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjNote, setAdjNote] = useState("");
  const [adjErr, setAdjErr] = useState("");
  const canAdjust = ["ADMIN", "OFFICE_LEAD"].includes(getRole() as string);
  const loadLoyalty = () => { if (id) api.get(`/loyalty/client/${id}`).then(r => setLoyalty(r.data)).catch(() => setLoyalty(null)); };
  useEffect(() => { if (activeTab === "bonus") loadLoyalty(); /* eslint-disable-next-line */ }, [activeTab, id]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      const [clientRes, dealsRes, tlRes] = await Promise.all([
        getClient(Number(id)),
        getClientDeals(Number(id)),
        getClientTimeline(Number(id)),
      ]);
      setClient(clientRes.data);
      setDeals(dealsRes.data);
      setTimeline(tlRes.data);
    };
    load();
  }, [id]);

  const enterCabinet = async () => {
    // вкладку открываем сразу по клику (иначе браузер блокирует всплывающее окно), адрес подставляем после ответа
    const win = window.open("", "_blank");
    try {
      const res = await api.post(`/cabinet/admin/impersonate/${id}`);
      if (win) win.location.href = res.data.url;
      else window.location.href = res.data.url;
    } catch (e: any) {
      win?.close();
      alert(e?.response?.data?.message ?? "Не удалось войти в кабинет клиента");
    }
  };

  const removeClient = async () => {
    if (!window.confirm(`Удалить клиента ${client.firstName} ${client.lastName}? Это действие нельзя отменить.`)) return;
    try {
      await deleteClient(Number(id));
      navigate("/clients");
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Не удалось удалить клиента");
    }
  };

  if (!client) return <div className="page-loading">Загрузка...</div>;

  const initials = (client.firstName?.[0] || "") + (client.lastName?.[0] || "");
  const fmtDate = (iso: string) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" });
  };
  const fmtMoney = (n: any) => (Number(n) || 0).toLocaleString("ru-RU") + " ₸";

  const activeDeals = deals.filter(d => d.status === "active");
  const closedDeals = deals.filter(d => d.status !== "active");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#0a0d14", overflow: "hidden" }}>

      {/* ── Topbar ── */}
      <div className="cp-topbar" style={{ padding: "10px 20px", background: "#0f1117", borderBottom: "1px solid #1f2937", flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: "#818cf8", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0 }}>
          ← Назад
        </button>
        <span style={{ color: "#374151" }}>/</span>
        <span className="cp-name" style={{ fontSize: 13, color: "#9ca3af" }}>{client.firstName} {client.lastName}</span>
        <div className="cp-spacer" style={{ flex: 1 }} />
        <button
          className="cp-btn"
          onClick={enterCabinet}
          title="Открыть личный кабинет клиента без отправки кода в WhatsApp (режим просмотра, 30 минут)"
          style={{ background: "transparent", color: "#f59e0b", border: "1px solid #f59e0b66", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          🔑 Войти в ЛК клиента
        </button>
        {getRole() === "ADMIN" && (
          <button
            className="cp-btn"
            onClick={removeClient}
            title="Удалить клиента (только администратор)"
            style={{ background: "transparent", color: "#ef4444", border: "1px solid #ef444466", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            🗑 Удалить
          </button>
        )}
        <button className="cp-btn" onClick={() => setShowEdit(true)} style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          ✏ Редактировать
        </button>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Профиль */}
          <div style={{ background: "#0f1117", border: "1px solid #1f2937", borderRadius: 12, padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                {initials}
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#f9fafb" }}>{client.firstName} {client.lastName}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
                  {client.tags?.length
                    ? client.tags.map((t: string) => <Tag key={t} text={t} />)
                    : <span style={{ fontSize: 12, color: "#4b5563" }}>Тегов нет</span>}
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "12px 24px" }}>
              {[
                { label: "Компания", value: client.companyName || "—" },
                { label: "Телефон",  value: client.phone || "—", phone: client.phone },
                { label: "WhatsApp", value: client.whatsappPhone || (client.phone ? `${client.phone} (как телефон)` : "—"), phone: client.whatsappPhone || client.phone },
                { label: "Email",    value: client.email || "—" },
                { label: "Адрес",    value: client.address || "—" },
                { label: "ИИН/БИН", value: client.inn || "—" },
              ].map(({ label, value, phone }: any) => (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.06em", color: "#6b7280", marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 13, color: "#e5e7eb", display: "flex", alignItems: "center", gap: 4 }}>
                    {value}
                    {phone && <ClickToCall phone={phone} />}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Вкладки */}
          <div style={{ background: "#0f1117", border: "1px solid #1f2937", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #1f2937" }}>
              {([
                { id: "deals",    label: `Сделки (${deals.length})` },
                { id: "history",  label: "История" },
                { id: "bonus",    label: "🪙 Бонусы" },
                { id: "comments", label: "Комментарии" },
              ] as const).map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: "10px 20px", border: "none", background: "transparent", cursor: "pointer",
                    fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
                    color: activeTab === tab.id ? "#818cf8" : "#6b7280",
                    borderBottom: activeTab === tab.id ? "2px solid #6366f1" : "2px solid transparent",
                    transition: "all 0.15s",
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ padding: "16px 20px" }}>

              {/* ── СДЕЛКИ ── */}
              {activeTab === "deals" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

                  {/* Активные */}
                  {activeDeals.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.07em", color: "#6b7280", marginBottom: 8 }}>
                        Активные ({activeDeals.length})
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {activeDeals.map(d => <DealRow key={d.id} d={d} fmtMoney={fmtMoney} fmtDate={fmtDate} onOpen={() => navigate(`/deals?deal=${d.id}`)} />)}
                      </div>
                    </div>
                  )}

                  {/* Завершённые */}
                  {closedDeals.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.07em", color: "#6b7280", marginBottom: 8 }}>
                        Завершённые ({closedDeals.length})
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {closedDeals.map(d => <DealRow key={d.id} d={d} fmtMoney={fmtMoney} fmtDate={fmtDate} onOpen={() => navigate(`/deals?deal=${d.id}`)} />)}
                      </div>
                    </div>
                  )}

                  {deals.length === 0 && (
                    <div style={{ padding: "30px 0", textAlign: "center", color: "#6b7280", fontSize: 13 }}>Сделок нет</div>
                  )}
                </div>
              )}

              {activeTab === "history" && <Timeline items={timeline} />}

              {/* ── БОНУСЫ И КУПОНЫ ── */}
              {activeTab === "bonus" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {!loyalty && <div style={{ padding: "20px 0", textAlign: "center", color: "#6b7280", fontSize: 13 }}>Загрузка…</div>}
                  {loyalty && (
                    <>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 160px", background: "#111827", borderRadius: 10, padding: "12px 16px" }}>
                          <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>БАЛАНС</div>
                          <div style={{ fontSize: 24, fontWeight: 700, color: "#fbbf24" }}>{fmtMoney(loyalty.balance)}</div>
                        </div>
                        <div style={{ flex: "1 1 160px", background: "#111827", borderRadius: 10, padding: "12px 16px" }}>
                          <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>СГОРИТ В БЛИЖАЙШИЕ 14 ДНЕЙ</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: loyalty.expiringSoon > 0 ? "#f87171" : "#9ca3af" }}>{fmtMoney(loyalty.expiringSoon)}</div>
                          {loyalty.expiringAt && <div style={{ fontSize: 11, color: "#6b7280" }}>с {fmtDate(loyalty.expiringAt)}</div>}
                        </div>
                      </div>
                      {!loyalty.settings?.enabled && <div style={{ fontSize: 12, color: "#fbbf24" }}>Бонусная программа сейчас выключена (раздел «Акции»).</div>}

                      {canAdjust && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <input placeholder="± сумма" inputMode="numeric" value={adjAmount} onChange={e => setAdjAmount(e.target.value.replace(/[^\d-]/g, ""))}
                            style={{ width: 110, background: "#1a2030", border: "1px solid #374151", borderRadius: 7, color: "#f9fafb", fontSize: 13, padding: "7px 10px", outline: "none" }} />
                          <input placeholder="Причина (подарок, исправление…)" value={adjNote} onChange={e => setAdjNote(e.target.value)}
                            style={{ flex: "1 1 200px", minWidth: 0, background: "#1a2030", border: "1px solid #374151", borderRadius: 7, color: "#f9fafb", fontSize: 13, padding: "7px 10px", outline: "none" }} />
                          <button onClick={async () => {
                            setAdjErr("");
                            try { await api.post(`/loyalty/client/${id}/adjust`, { amount: Number(adjAmount), note: adjNote }); setAdjAmount(""); setAdjNote(""); loadLoyalty(); }
                            catch (e: any) { setAdjErr(e?.response?.data?.message ?? "Не удалось"); }
                          }} disabled={!adjAmount || adjAmount === "-"}
                            style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: !adjAmount || adjAmount === "-" ? 0.5 : 1 }}>Изменить баланс</button>
                          {adjErr && <span style={{ fontSize: 12, color: "#f87171" }}>{adjErr}</span>}
                        </div>
                      )}

                      {loyalty.coupons?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.07em", color: "#6b7280", marginBottom: 8 }}>Купоны</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {loyalty.coupons.map((c: any) => (
                              <div key={c.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", background: "#111827", borderRadius: 8, padding: "8px 12px", fontSize: 13 }}>
                                <b style={{ color: "#f9fafb", letterSpacing: 1 }}>{c.code}</b>
                                <span style={{ color: "#10b981" }}>−{Number(c.percent)}%</span>
                                <span style={{ color: "#9ca3af" }}>до {fmtDate(c.validUntil)}</span>
                                <span style={{ marginLeft: "auto", color: c.status === "active" ? "#10b981" : "#6b7280" }}>{c.status === "active" ? "действует" : c.status === "used" ? "использован" : c.status}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.07em", color: "#6b7280", marginBottom: 8 }}>История бонусов</div>
                        {loyalty.history.length === 0 ? <div style={{ fontSize: 13, color: "#6b7280" }}>Операций пока нет</div> : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {loyalty.history.map((h: any) => {
                              const label: Record<string, string> = { accrual: "Начисление", spend: "Списание", spend_void: "Списание отменено", refund: "Возврат", expire: "Сгорело", adjust: "Корректировка", accrual_void: "Начисление отменено" };
                              return (
                                <div key={h.id} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", padding: "6px 0", borderBottom: "1px solid #1f2937", fontSize: 13 }}>
                                  <span style={{ color: "#6b7280", minWidth: 92 }}>{fmtDate(h.createdAt)}</span>
                                  <span style={{ color: "#e5e7eb" }}>{label[h.type] ?? h.type}{h.dealNumber ? ` · ${h.dealNumber}` : ""}</span>
                                  <span style={{ color: "#6b7280", flex: "1 1 120px", minWidth: 0, overflowWrap: "anywhere" }}>{h.note ?? ""}</span>
                                  {h.type !== "spend_void" && h.type !== "accrual_void" && <b style={{ color: h.amount >= 0 ? "#10b981" : "#f87171", marginLeft: "auto" }}>{h.amount >= 0 ? "+" : ""}{Number(h.amount).toLocaleString("ru-RU")}</b>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "comments" && (
                <div style={{ padding: "30px 0", textAlign: "center", color: "#6b7280", fontSize: 13 }}>Комментарии пока пусты</div>
              )}
            </div>
          </div>

        </div>
      </div>

      {showEdit && (
        <EditClientModal
          client={client}
          onClose={() => setShowEdit(false)}
          onUpdated={async () => {
            if (!id) return;
            const res = await getClient(Number(id));
            setClient(res.data);
          }}
        />
      )}
    </div>
  );
}

function DealRow({ d, fmtMoney, fmtDate, onOpen }: { d: any; fmtMoney: (n: any) => string; fmtDate: (s: string) => string; onOpen?: () => void }) {
  const st = STATUS_MAP[d.status] ?? STATUS_MAP.active;
  const pay = d.paymentMethod ? PAY_LABELS[d.paymentMethod] : null;
  const amount = d.amount ?? d.price ?? 0;

  return (
    <div className="cp-deal-row" onClick={onOpen} style={{ background: "#131926", border: "1px solid #1f2937", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, transition: "border-color 0.15s", cursor: onOpen ? "pointer" : "default" }}
      onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = "#374151"}
      onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = "#1f2937"}
    >
      {/* Статус-полоска */}
      <div style={{ width: 3, height: 36, borderRadius: 2, background: st.color, flexShrink: 0 }} />

      {/* Основное */}
      <div className="cp-deal-main" style={{ flex: 1, minWidth: 0 }}>
        <div className="cp-deal-title" style={{ fontSize: 13, fontWeight: 600, color: "#f9fafb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</div>
        <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
          {d.number && <span style={{ fontSize: 11, color: "#4b5563" }}>{d.number}</span>}
          <span style={{ fontSize: 11, color: "#6b7280" }}>{fmtDate(d.createdAt)}</span>
          {pay && <span style={{ fontSize: 11, color: "#9ca3af" }}>{pay.icon} {pay.label}</span>}
        </div>
      </div>

      {/* Сумма */}
      <div style={{ fontSize: 14, fontWeight: 700, color: "#c084fc", flexShrink: 0 }}>{fmtMoney(amount)}</div>

      {/* Статус-бейдж */}
      <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: st.bg, color: st.color, flexShrink: 0 }}>
        {st.label}
      </span>
    </div>
  );
}
